import { BlankTransport, type LogLayerTransport } from "@-/com.npmjs.loglayer";
import onetime from "@-/com.npmjs.onetime";
import type { AppLogLevel } from "#app-log-layer-common";

/**
 * One structured log as seen by the app LogLayer, after prefixing and error serialization. This
 * is the shape the diagnostics recorder (`lib/diagnostics`) buffers; it deliberately carries the
 * already-serialized error (a plain object) rather than the `Error` instance.
 */
export type AppLogRecord = {
  data: unknown;
  error: unknown;
  level: AppLogLevel;
  messages: readonly unknown[];
  timestamp: number;
};

export type AppLogListener = (record: AppLogRecord) => void;

const getListeners = onetime((): Set<AppLogListener> => new Set());

/**
 * Subscribe to every log that flows through the app LogLayer (from `logger(import.meta.location)`
 * calls in production, and from consola in every build). Returns the unsubscribe function. The
 * transport short-circuits when nobody is listening, so an idle registry costs one `Set.size` read
 * per log.
 */
export function addAppLogListener(listener: AppLogListener): () => void {
  const listeners = getListeners();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function hasAppLogListeners(): boolean {
  return getListeners().size > 0;
}

function toAppLogLevel(logLevel: string): AppLogLevel {
  switch (logLevel) {
    case "trace":
    case "debug":
    case "info":
    case "warn":
    case "error":
    case "fatal": {
      return logLevel;
    }
    default: {
      return "info";
    }
  }
}

/**
 * A LogLayer transport that fans each log out to the registered {@link AppLogListener}s. It
 * never throws into LogLayer: a listener that throws is isolated so one broken subscriber cannot
 * take down logging for the whole app.
 */
export function createAppLogListenerTransport(): LogLayerTransport {
  return new BlankTransport({
    enabled: true,
    shipToLogger(params) {
      const listeners = getListeners();
      if (listeners.size === 0) {
        return params.messages;
      }
      const record: AppLogRecord = {
        data: params.hasData ? params.data : undefined,
        error: params.error,
        level: toAppLogLevel(params.logLevel),
        messages: params.messages,
        timestamp: Date.now(),
      };
      for (const listener of listeners) {
        try {
          listener(record);
        } catch {
          // A diagnostics subscriber must never break logging.
        }
      }
      return params.messages;
    },
  });
}
